<script lang="ts" setup>
  import { useQuery } from '@tanstack/vue-query'
  import { onClickOutside } from '@vueuse/core'

  import type { UserProfileOut } from '@/shared/api/intranet'

  import { listUserProfilesPage } from '@/entities/profile'

  import DynamicSearchItem from './DynamicSearchItem.vue'

  const props = defineProps<{
    searchInputInFocus: boolean
    searchPage?: boolean
    searchQuery?: string
  }>()

  const emit = defineEmits<{
    (e: 'select-item'): void
    (e: 'blur'): void
  }>()

  const DYNAMIC_SEARCH_PAGE_SIZE = 10
  const DYNAMIC_SEARCH_STALE_MS = 60000

  const dynamicSearch = ref()
  const isResultsPanelDismissed = ref(false)

  const isSearchActive = computed(
    () => !!props.searchQuery && props.searchQuery.length >= 2 && props.searchInputInFocus,
  )

  const dynamicSearchQueryKey = computed(() => ({
    query: props.searchQuery ?? '',
    pageSize: DYNAMIC_SEARCH_PAGE_SIZE,
    is_blocked: false,
  }))

  const { data, isLoading } = useQuery({
    queryKey: ['userProfilesDynamicSearch', dynamicSearchQueryKey],
    queryFn: () =>
      listUserProfilesPage({
        page: {
          number: 1,
          size: DYNAMIC_SEARCH_PAGE_SIZE,
        },
        filter: {
          query: props.searchQuery!,
          is_blocked: false,
        },
      }),
    enabled: isSearchActive,
    staleTime: DYNAMIC_SEARCH_STALE_MS,
  })

  watch(
    () => props.searchQuery,
    () => (isResultsPanelDismissed.value = false),
  )

  watch(
    () => props.searchInputInFocus,
    (focused) => {
      if (!focused) {
        isResultsPanelDismissed.value = false
      }
    },
  )

  const profiles = computed<UserProfileOut[]>(() => {
    if (!isSearchActive.value) {
      return []
    }
    return data.value?.userProfiles ?? []
  })

  const showDynamicSearch = computed(() => isSearchActive.value && !isResultsPanelDismissed.value)

  onClickOutside(dynamicSearch, () => emit('blur'))

  const handleSelectItem = () => {
    isResultsPanelDismissed.value = true
    emit('select-item')
  }
</script>

<template>
  <div
    v-if="showDynamicSearch"
    ref="dynamicSearch"
    class="bg-popover-bg shadow-popover rounded-10 absolute left-0 z-5 mt-1 box-border w-full border p-1"
    :class="{
      '': searchPage,
      'min-h-17.5': !profiles.length,
    }"
  >
    <NScrollbar class="max-h-63">
      <div class="flex flex-col items-start justify-start">
        <template v-if="profiles.length">
          <DynamicSearchItem
            v-for="profile in profiles"
            :key="profile.id"
            :profile="profile"
            @click="handleSelectItem"
          />
        </template>
        <div
          v-else-if="!isLoading"
          class="w-full px-1.5 pt-4 pb-0 text-center text-(--color-invert)"
        >
          {{ $t('profiles_search_not_found') }}
        </div>
      </div>
    </NScrollbar>
  </div>
</template>
